import React from 'react';
import styled from '@emotion/styled';
import { FormComponentProps } from 'antd/lib/form';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import { Button, Classes, Dialog, Intent, Card } from '@blueprintjs/core';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { ApplicationStore, AuthenticationStore } from '../../store';
import { Form } from 'react-final-form';
import { TextInputFormField, PasswordInputFormField } from '../common/form';
import { TLCode } from '../../common/enums';
import { IconNames } from '@blueprintjs/icons';
import TranslatedMessage from '../common/translation/TranslatedMessage';

const LoginCard = styled(Card)`
  width: 24rem;
`;

const LoginContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 33%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivCenter = styled.div`
  display: flex;
  flex-direction: row;
  width: 33%;
  padding-bottom: 5px;
  justify-content: center;
  .bp3-form-content > div {
    display: flex;
    flex-direction: row;
    margin-right: 20px;
    .bp3-control {
      margin-top: 0;
      margin-right: 5px;
    }
  }
  .bp3-form-group {
    margin-bottom: 0;
  }
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: row;
  width: 33%;
  padding-bottom: 5px;
  margin-leftt: 5%;
  .bp3-button {
    margin-right: 3px;
  }
`;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some((field) => fieldsError[field]);
}

interface LoginProps extends FormComponentProps {
  handleLogin: (data: any) => void;
  handleChangePassword: (data: any) => void;
  handleResetPassword: (data: any) => void;
  appVersion: string;
  webApiVersion: string | undefined;
}

interface LoginState {
  isChangePasswordOpen: boolean;
  isResetPasswordOpen: boolean;
}

interface LoginPageProps extends WithTranslateProps, FormComponentProps {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

class Login extends React.Component<LoginProps, LoginState> {
  state = {
    isChangePasswordOpen: false,
    isResetPasswordOpen: false,
  } as LoginState;

  startChange = () => {
    this.setState({ isChangePasswordOpen: true });
  };

  stopChange = () => {
    this.setState({ isChangePasswordOpen: false });
  };

  startReset = () => {
    this.setState({ isResetPasswordOpen: true });
  };

  stopReset = () => {
    this.setState({ isResetPasswordOpen: false });
  };

  changePassWordSubmit = (data) => {
    this.props.handleChangePassword(data);
    this.stopChange();
  };

  resetPassWordSubmit = (data) => {
    this.props.handleResetPassword(data);
    this.stopReset();
  };

  render() {
    const { handleLogin, handleChangePassword, handleResetPassword, appVersion, webApiVersion } = this.props;
    const { isChangePasswordOpen, isResetPasswordOpen } = this.state;
    return (
      <LoginContainer>
        <LoginCard elevation={3}>
          <Container>
            <Form
              onSubmit={handleLogin}
              render={({ handleSubmit, form, hasValidationErrors, dirty }) => (
                <form onSubmit={handleSubmit}>
                  <h1>
                    Pharmagarde
                    <br />
                  </h1>
                  UI v{appVersion}
                  <br />
                  WebApi v{webApiVersion}
                  <br />
                  <TextInputFormField
                    name="username"
                    required={true}
                    inline={true}
                    fill={true}
                    validate={[]}
                    placeholder="nr APB"
                  />
                  <PasswordInputFormField
                    name="password"
                    required={true}
                    inline={true}
                    fill={true}
                    placeholder="Password"
                  />
                  <DivLine>
                    <DivLeft>
                      {
                        <a onClick={() => this.startChange()}>
                          <TranslatedMessage id={TLCode.LOGIN_ChangePassword} />
                        </a>
                      }
                    </DivLeft>
                    <DivCenter>
                      {
                        <Button
                          type={'submit'}
                          text="Log in"
                          intent={Intent.PRIMARY}
                          disabled={hasValidationErrors || !dirty}
                        />
                      }
                    </DivCenter>
                    <DivRight>
                      {
                        <a onClick={() => this.startReset()}>
                          <TranslatedMessage id={TLCode.LOGIN_ForgotPassword} />
                        </a>
                      }
                    </DivRight>
                  </DivLine>
                  <DivLine>
                    <TranslatedMessage id={TLCode.LOGIN_PreferredBrowsers} />
                  </DivLine>
                  <Dialog
                    isOpen={isChangePasswordOpen}
                    onClose={this.stopChange}
                    icon={IconNames.EDIT}
                    title={<TranslatedMessage id={TLCode.LOGIN_ChangePassword} />}
                  >
                    <Form
                      onSubmit={this.changePassWordSubmit}
                      render={({ handleSubmit, form, hasValidationErrors, dirty }) => (
                        <form onSubmit={handleSubmit}>
                          <Container>
                            <br />
                            <TextInputFormField
                              name="username"
                              required={true}
                              inline={true}
                              fill={true}
                              validate={[]}
                              placeholder="nr APB"
                            />
                            <PasswordInputFormField
                              name="password"
                              required={true}
                              inline={true}
                              fill={true}
                              placeholder="Current password"
                            />
                            <PasswordInputFormField
                              name="newPwd1"
                              required={true}
                              inline={true}
                              fill={true}
                              placeholder="New password"
                            />
                            <PasswordInputFormField
                              name="newPwd2"
                              required={true}
                              inline={true}
                              fill={true}
                              placeholder="Check new password"
                            />
                            <div className={Classes.DIALOG_FOOTER}>
                              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                <Button
                                  text={<TranslatedMessage id={TLCode.GENERAL_Cancel} />}
                                  onClick={this.stopChange}
                                />
                                <Button
                                  type="submit"
                                  intent={Intent.PRIMARY}
                                  disabled={hasValidationErrors}
                                  text={<TranslatedMessage id={TLCode.SECRETARIAT_DASHBOARD_ANNUAL_REVIEW_Validate} />}
                                />
                              </div>
                            </div>
                          </Container>
                        </form>
                      )}
                    />
                  </Dialog>
                  <Dialog
                    isOpen={isResetPasswordOpen}
                    onClose={this.stopReset}
                    icon={IconNames.EDIT}
                    title={<TranslatedMessage id={TLCode.LOGIN_ChangePassword} />}
                  >
                    <Form
                      onSubmit={this.resetPassWordSubmit}
                      render={({ handleSubmit, form, hasValidationErrors, dirty }) => (
                        <form onSubmit={handleSubmit}>
                          <Container>
                            <br />
                            <TextInputFormField
                              name="username"
                              required={true}
                              inline={true}
                              fill={true}
                              validate={[]}
                              placeholder="nr APB"
                            />
                            <TextInputFormField
                              name="email"
                              required={true}
                              inline={true}
                              fill={true}
                              validate={[]}
                              placeholder="Email"
                            />
                            <DivLine>
                              <TranslatedMessage id={TLCode.LOGIN_EmailAPB} />
                            </DivLine>
                            <br />
                            <div className={Classes.DIALOG_FOOTER}>
                              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                <Button
                                  text={<TranslatedMessage id={TLCode.GENERAL_Cancel} />}
                                  onClick={this.stopReset}
                                />
                                <Button
                                  type="submit"
                                  intent={Intent.PRIMARY}
                                  disabled={hasValidationErrors}
                                  text={<TranslatedMessage id={TLCode.GENERAL_Validate} />}
                                />
                              </div>
                            </div>
                          </Container>
                        </form>
                      )}
                    />
                  </Dialog>
                </form>
              )}
            />
          </Container>
        </LoginCard>
      </LoginContainer>
    );
  }
}

@inject('AuthenticationStore', 'ApplicationStore')
@observer
class LoginPage extends React.Component<LoginPageProps> {
  constructor(props: LoginPageProps) {
    super(props);
    this.props.AuthenticationStore!.logout();
  }

  render() {
    const props = this.props;
    return (
      <Login
        handleLogin={(data) => {
          this.props.AuthenticationStore!.authenticate(data.username, data.password);
        }}
        handleChangePassword={(data) => {
          this.props.AuthenticationStore!.changePassword(data.username, data.password, data.newPwd1, data.newPwd2);
        }}
        handleResetPassword={(data) => {
          this.props.AuthenticationStore!.resetPassword(data.username, data.email);
        }}
        appVersion={this.props.ApplicationStore!.appVersion}
        webApiVersion={this.props.ApplicationStore!.backendVersion}
        {...props}
      />
    );
  }
}

export default withTranslate(LoginPage);
